<!-- src/components/CategoryNav.vue -->
<template>
  <nav class="mega-menu" @mouseleave="closeAllDropdowns">
    <ul class="top-level">
      <!-- Loop over top-level nodes in the tree -->
      <li
        v-for="(nodeObj, nodeId) in topLevelNodes"
        :key="nodeId"
        class="nav-item"
        @mouseenter="onItemEnter(nodeId)"
        @mouseleave="onItemLeave(nodeId)"
      >
        <!-- The main label for the category. Clicking it can navigate directly. -->
        <div class="nav-link" @click.stop="navigate([nodeId])">
          {{ nodeId }}
          <span class="count">({{ nodeObj.__count }})</span>
        </div>

        <!-- The dropdown sub-menu, only visible if openDropdown===nodeId & has children -->
        <transition name="fade">
          <div
            v-if="openDropdown === nodeId && hasChildren(nodeObj)"
            class="submenu"
          >
            <!-- We pass nodeId/nodeObj to SubMenu, which recursively displays deeper levels -->
            <SubMenu
              :node-id="nodeId"
              :node-obj="nodeObj"
              :parent-path="[]"
              @navigate="navigate"
            />
          </div>
        </transition>
      </li>
    </ul>
  </nav>
</template>

<script>
// We do NOT import SubMenu from './SubMenu.vue' to avoid circular import in SubMenu itself.
// Instead, we define "SubMenu" as a global or separate component. But let's do it the standard way:

import SubMenu from './SubMenu.vue';

export default {
  name: 'CategoryNav',
  components: { SubMenu },
  props: {
    tree: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      openDropdown: null,  // which top-level item is hovered
      closeDelay: 800,
      closeTimer: null
    };
  },
  computed: {
    topLevelNodes() {
      // e.g. { Web: {...}, men: {...}, women: {...} }
      return this.tree;
    }
  },
  methods: {
    hasChildren(nodeObj) {
      return nodeObj && nodeObj.__children && Object.keys(nodeObj.__children).length > 0;
    },
    navigate(pathArr) {
      // pathArr might be ["Web", "men", "men-clothing"]
      this.$emit('navigate', pathArr);
    },

    onItemEnter(nodeId) {
      // Cancel any pending close
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.openDropdown = nodeId;
    },
    onItemLeave(nodeId) {
      // Start a delayed close so you can move horizontally
      this.closeTimer = setTimeout(() => {
        if (this.openDropdown === nodeId) {
          this.openDropdown = null;
        }
      }, this.closeDelay);
    },
    closeAllDropdowns() {
      // If user leaves the entire nav
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.openDropdown = null;
    }
  }
};
</script>

<style scoped>
.mega-menu {
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  padding: 0.5rem 1.5rem;
  font-family: sans-serif;
  position: relative;
}

.top-level {
  display: flex;
  gap: 1rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  position: relative;
}

.nav-link {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-weight: 600;
  color: #333;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.nav-link:hover {
  background-color: #e2e8f0; /* light gray/blue */
}

.count {
  font-weight: normal;
  color: #777;
  margin-left: 4px;
  font-size: 0.85rem;
}

/* Submenu container */
.submenu {
  position: absolute;
  top: 2.4rem; /* ~ the height of nav-link */
  left: 0;
  background: #fff;
  border: 1px solid #ddd;
  min-width: 220px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  padding: 0.75rem;
  z-index: 999;
}

/* Fade transition for submenus */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
